import React, { useState } from 'react';

import { Checkbox } from 'antd';
import styled from 'styled-components';

import { isMysteryShopAdmin } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import EditShopRequestModal from '../EditShopRequestModal/EditShopRequestModal';
import { useShopRequestsDataContext } from '../ShopRequestsDataContext';
import ShopRequestCommunity from '../TableColumns/ShopRequestCommunity';
import ShopRequestsDueDate from '../TableColumns/ShopRequestsDueDate';
import ShopRequestsPropagation from '../TableColumns/ShopRequestsPropagation';
import ShopRequestsStatus from '../TableColumns/ShopRequestsStatus';
import ShopRequestsUpdatedAt from '../TableColumns/ShopRequestsUpdatedAt';
import ShopRequestsUserAssignment from '../TableColumns/ShopRequestsUserAssignment';
import { MysteryShopRequestInbound } from '../types';

const Container = styled.div<{ $canSeePropagation: boolean }>`
  display: grid;
  grid-template-columns: ${({ $canSeePropagation }) =>
    $canSeePropagation ? '32px calc(28% - 32px) 12% 16% 18% 12% 14%' : '32px calc(28% - 32px) 16% 18% 12% 26%'};
  background: white;
  border: solid 1px var(--line-gray);
  border-top: 0;
`;

const SelectionColumn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SubContainer = styled.div`
  display: contents;

  .due-date-column,
  .updated-at-column,
  .status-column,
  .community-column,
  .propagation-column,
  .shopper-column {
    min-width: 0;
    padding: 10px 0 10px 20px;
  }
`;

type Props = {
  shopRequest: MysteryShopRequestInbound;
  refetch: () => void;
};

const ShopRequestGroupTableRow = ({ shopRequest, refetch }: Props) => {
  const { currentUser } = useAppSelector((state) => state.auth);
  const [modifyingShopRequest, setModifyingShopRequest] = useState<MysteryShopRequestInbound | null>(null);
  const { selectionMode, selectedItemsIds, setSelectedItemsIds } = useShopRequestsDataContext();

  const onClick = () => {
    setModifyingShopRequest(shopRequest);
  };

  const onSelect = (record: any, selected: any) => {
    const itemKey = record.id;
    let newSelection: any[] = [];

    if (selected) {
      newSelection = [...selectedItemsIds, itemKey];
    } else {
      const index = selectedItemsIds.indexOf(itemKey);
      if (index > -1) {
        newSelection = [...selectedItemsIds];
        newSelection.splice(index, 1);
      }
    }

    setSelectedItemsIds(newSelection);
  };

  const isRowSelected = selectedItemsIds.some((id) => id.toString() === shopRequest.id.toString());
  const canSeePropagation = currentUser && (currentUser.isSuperuser || isMysteryShopAdmin(currentUser));

  return (
    <>
      <Container className="shop-request-group-table-row" $canSeePropagation={Boolean(canSeePropagation)}>
        <SelectionColumn>
          {selectionMode && (
            <Checkbox checked={isRowSelected} onChange={(e: any) => onSelect(shopRequest, e.target.checked)} />
          )}
        </SelectionColumn>
        <SubContainer onClick={onClick}>
          <ShopRequestCommunity shopRequest={shopRequest} />
          {canSeePropagation && (
            <div className="propagation-column">
              <ShopRequestsPropagation shopRequest={shopRequest} />
            </div>
          )}
          <div className="shopper-column">
            <ShopRequestsUserAssignment
              shopRequest={shopRequest}
              fieldKey="shopper"
              fieldKeyId="shopper_id"
              label="Shopper"
            />
          </div>
          <ShopRequestsStatus shopRequest={shopRequest} />
          <ShopRequestsUpdatedAt shopRequest={shopRequest} />
          <ShopRequestsDueDate shopRequest={shopRequest} />
        </SubContainer>
      </Container>
      {modifyingShopRequest && (
        <EditShopRequestModal
          shopRequest={modifyingShopRequest}
          refetchData={refetch}
          onCancel={() => setModifyingShopRequest(null)}
        />
      )}
    </>
  );
};

export default ShopRequestGroupTableRow;
